import { formatChord, normalizeChord, parseChord } from '../model/chord';
import { defaultKeybindings } from '../model/defaultKeybindings';
import { findReservedChord } from '../model/reservedChords';
import { losesToSavedOverride, type StoredOverrides } from '../model/savedOverrideLosses';
import type {
  KeybindingCommandId,
  KeybindingNotice,
  KeyboardLayoutSnapshot,
  Keymap,
  Platform,
  SchemeId,
} from '../model/keybindingTypes';
import { formattedChord } from '../hooks/useShortcutLabel';
import { availableKeybindingSchemes, schemeFor } from '../store/keybindingSchemes';
import { SCOPE_CONTEXT } from './keybindingScopes';

const ROLE_LABELS: Readonly<Record<string, string>> = {
  close: 'Close Window',
  undo: 'Undo',
  redo: 'Redo',
  cut: 'Cut',
  copy: 'Copy',
  paste: 'Paste',
  selectAll: 'Select All',
  resetZoom: 'Actual Size',
  zoomIn: 'Zoom In',
  zoomOut: 'Zoom Out',
  minimize: 'Minimize',
  togglefullscreen: 'Full Screen',
  quit: 'Quit',
  hide: 'Hide',
  hideOthers: 'Hide Others',
  reload: 'Reload',
  forceReload: 'Force Reload',
  toggleDevTools: 'Developer Tools',
};

/** "reserved for Quit" / "reserved by the operating system". */
export function reservedByText(role: string | undefined): string {
  return role ? `reserved for ${ROLE_LABELS[role] ?? role}` : 'reserved by the operating system';
}

export type NoticeTextContext = {
  readonly platform: Platform;
  readonly isDev: boolean;
  readonly layout: KeyboardLayoutSnapshot;
  readonly keymap: Keymap;
  /** The saved overrides: "It stays saved." only for a chord the row has saved. */
  readonly overrides: StoredOverrides;
};

/** The row has a saved override that holds this chord. */
function isSavedChord(notice: KeybindingNotice, context: NoticeTextContext): boolean {
  const stored = context.overrides[notice.commandId];
  return Array.isArray(stored) && stored.some((chord) => (normalizeChord(chord) ?? chord) === notice.chord);
}

/**
 * For a crossed-out chip on a row that still uses its defaults, lost to
 * another command's saved shortcut: any change to the row's shortcuts — the
 * user's own edit, or another command taking one of its active keys — leaves
 * it out (O1), and Reset takes the scheme's keys back from the other command.
 * Not a layout-dependent conflict: that loss is never durable, so after Reset
 * the saved shortcut still outranks the chip on this layout (FR11).
 */
function notKeptHint(notice: KeybindingNotice, context: NoticeTextContext): string {
  return !Array.isArray(context.overrides[notice.commandId]) && losesToSavedOverride(notice, context.overrides)
    ? ' If this command\'s shortcuts change, its crossed-out shortcut is not kept; Reset brings the scheme\'s'
      + ' shortcuts back and takes them from any other command that uses them, except a conflict caused by your'
      + ' keyboard layout, which needs different keys.'
    : '';
}

const labelOf = (commandId: KeybindingNotice['commandId']): string => defaultKeybindings[commandId].label;

function overlapText(notice: KeybindingNotice, chord: string): string {
  const other = notice.relatedCommandId;
  if (!other) return `${chord} is shared with a global command; the scoped command wins in its own context.`;
  const [scoped, global] = defaultKeybindings[notice.commandId].scope === 'global'
    ? [other, notice.commandId]
    : [notice.commandId, other];
  const context = SCOPE_CONTEXT[defaultKeybindings[scoped].scope];
  return `${chord} is shared by ${labelOf(scoped)} and ${labelOf(global)}. ${labelOf(scoped)} takes it in ${context}; ${labelOf(global)} keeps it everywhere else.`;
}

/**
 * Plain-language text for a validation notice. Validation's own messages use
 * canonical chord spellings (`Mod+J`); these use the platform's labels.
 */
export function noticeText(notice: KeybindingNotice, context: NoticeTextContext): string {
  const chord = formattedChord(notice.chord, context.platform, context.layout);
  const label = labelOf(notice.commandId);
  const reservedRole = () => findReservedChord(notice.chord, context.platform, context.isDev, context.layout)?.role;
  switch (notice.kind) {
    case 'collision': {
      if (!notice.relatedCommandId) return `${chord} was removed from ${label}.`;
      if (!notice.persisted) {
        const saved = isSavedChord(notice, context) ? ' It stays saved.' : '';
        return `On this platform ${chord} is the same shortcut as ${labelOf(notice.relatedCommandId)}'s, so it does nothing here.${saved}${notKeptHint(notice, context)}`;
      }
      const unbound = context.keymap[notice.commandId].length === 0 ? ` ${label} is now unbound.` : '';
      return `${chord} moved from ${label} to ${labelOf(notice.relatedCommandId)}.${unbound}`;
    }
    case 'default-shadowed':
      // A scheme or Jam default that one of the user's own shortcuts uses: inactive, never written.
      return notice.relatedCommandId
        ? `${labelOf(notice.relatedCommandId)} uses ${chord}, so it does nothing here.${notKeptHint(notice, context)}`
        : `${chord} is used by another command, so it does nothing here.`;
    case 'reserved':
      return notice.persisted
        ? `${chord} was removed from ${label}: it is ${reservedByText(reservedRole())}.`
        : `${chord} is ${reservedByText(reservedRole())} on this platform, so it does nothing here.${isSavedChord(notice, context) ? ' It stays saved.' : ''}`;
    case 'layout-collision':
      return notice.relatedCommandId
        ? `On your keyboard layout ${chord} is the same key as ${labelOf(notice.relatedCommandId)}'s shortcut, so it is inactive here. Choose different keys.${notKeptHint(notice, context)}`
        : `On your keyboard layout ${chord} is a reserved system shortcut, so it is inactive here. Choose different keys.`;
    case 'duplicate': {
      const other = notice.relatedChord ?? '';
      const where = parseChord(other)?.physical || parseChord(notice.chord)?.physical ? 'on this keyboard layout' : 'on this platform';
      return `Same keys as ${other ? formattedChord(other, context.platform, context.layout) : 'another shortcut of this command'} ${where}.`;
    }
    case 'scoped-global-overlap':
      return overlapText(notice, chord);
    case 'reserved-scope-exception':
      return `${chord} is also ${reservedByText(reservedRole())}; ${label} takes it only in ${SCOPE_CONTEXT[defaultKeybindings[notice.commandId].scope]}.`;
    case 'monaco-shadow':
      return `${chord} replaces the SQL editor's own shortcut for these keys while the editor has focus.`;
    case 'terminal-shell-key':
      return `${chord} is also a shell key; ${label} takes it while the terminal has focus.`;
    case 'mac-option':
      return 'Option combinations type characters in text fields; this binding only applies in its scope.';
  }
}

/**
 * Orchestrator ruling: only the Monaco-shadow warning is filtered, and only on
 * a chord that is the command's own scheme default — the docs already state
 * that cost for the defaults. Every other warning (a reserved-shortcut scope
 * exception, a shell key, an Option key, a scoped-vs-global overlap) still
 * shows on default rows, and a Monaco-shadow warning shows for any chord the
 * user recorded.
 */
export function isShownWarning(notice: KeybindingNotice, schemeId: SchemeId, targetPlatform: Platform): boolean {
  if (notice.kind !== 'monaco-shadow') return true;
  const defaults = schemeFor(schemeId, availableKeybindingSchemes).defaults[notice.commandId][targetPlatform];
  return !defaults.some((chord) => (normalizeChord(chord) ?? chord) === notice.chord);
}

const squash = (value: string): string => value.toLowerCase().replace(/\s+/g, '');

/**
 * Search matches the action label, the command id, and every spelling of its
 * chords — the canonical form (`Mod+Enter`) plus how this platform shows it
 * (`Ctrl+Enter`, `⌘↩`, a physical key's layout label).
 */
export function matchesKeybindingSearch(
  commandId: KeybindingCommandId,
  chords: readonly string[],
  query: string,
  targetPlatform: Platform,
  layout: KeyboardLayoutSnapshot,
): boolean {
  const needle = squash(query);
  if (!needle) return true;
  const haystack = [
    defaultKeybindings[commandId].label,
    commandId,
    ...chords,
    ...chords.map((chord) => formatChord(chord, targetPlatform)),
    ...chords.map((chord) => formattedChord(chord, targetPlatform, layout)),
  ];
  return haystack.some((value) => squash(value).includes(needle));
}
